import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, User } from "lucide-react";
import CTABand from "@/components/CTABand";
import { Reveal, easeLux } from "@/lib/motion";
import api from "@/lib/api";

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="relative z-10 min-h-[70vh] grid place-items-center px-6">
        <div className="text-center">
          <h1 className="font-display text-4xl mb-4">Article not found</h1>
          <Link to="/blog" className="text-gold link-underline">Back to journal</Link>
        </div>
      </div>
    );
  }

  if (!post) return <div className="min-h-[70vh]" />;

  return (
    <div className="relative z-10">
      <header className="relative pt-40 pb-16 md:pt-48 overflow-hidden">
        <motion.div initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 0.3 }} transition={{ duration: 1.6, ease: easeLux }}
          className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${post.cover_image})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060A17]/70 via-[#060A17]/85 to-[#060A17]" />
        <div className="relative mx-auto max-w-3xl px-6 md:px-8">
          <button onClick={() => navigate("/blog")} className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-gold transition-colors mb-8" data-testid="blog-back">
            <ArrowLeft size={16} /> Back to Journal
          </button>
          <span className="rounded-full bg-primary-green/90 px-3 py-1 text-xs text-white">{post.category}</span>
          <h1 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight mt-5">{post.title}</h1>
          <div className="flex items-center gap-6 mt-6 text-sm text-[#94A3B8]">
            <span className="flex items-center gap-2"><User size={15} /> {post.author}</span>
            <span className="flex items-center gap-2"><Calendar size={15} /> {new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
          </div>
        </div>
      </header>

      <article className="py-10 md:py-16">
        <div className="mx-auto max-w-3xl px-6 md:px-8">
          <Reveal>
            <p className="font-display text-2xl text-[#CBD5E1] leading-relaxed italic mb-8">{post.excerpt}</p>
            <div className="prose-invert text-[#CBD5E1] text-lg leading-[1.9] space-y-6">
              {post.content.split("\n").filter(Boolean).map((para, i) => <p key={i}>{para}</p>)}
            </div>
          </Reveal>
        </div>
      </article>
      <CTABand />
    </div>
  );
}
